import { Typography } from '../../../../core/components/molecules/Typography';
import { useMaterial } from '../../hooks/useMaterials';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMaterialStore } from '../../stores/materialStore';
import { Colors, IMaterial } from '../../../../core/models/disciplines';
import {
  getViewedFromResponse,
  materialViewTypeFromCategory,
} from '../../../../core/models/contentView';
import { useNewSubjectStore } from '../../stores/newSubjectStore';
import { useFilterConfigStore } from '../../stores/filterConfig';
import { IResponseFavorite } from '../../hooks/useContentFavorite';
import { useFinishMaterial } from '../../../../core/hooks/useFinishMaterial';
import SectionHeader from '../sectionHeader';
import CardLoader from '../cardLoading';
import ContentCard from '../../../../core/components/organism/ContentCard';
import getSubjectByFilter from '../../../../core/commom/getSubjectByFilter';
import FilterModal from '../ModalFilter';
import { useMaterialRequestStore } from '../../stores/materialRequestStore';
import { useAccess } from '../../../../core/accessControl/useAccess';
import { openFreemiumUpsell } from '../../../../core/stores/freemiumUpsellStore';

interface IMaterialList {
  module: string;
  discipline: Colors;
  url: string;
}

export default function MaterialList({ module, discipline, url }: IMaterialList) {
  const navigate = useNavigate();

  const { mutate, isPending } = useMaterial();
  const { setFavorite: setStoredFavorite } = useMaterialRequestStore();
  const { resetFilters, status, favorites } = useFilterConfigStore();
  const { selectedSubjects } = useNewSubjectStore();
  const { materials, setAllMaterials, setMaterials, setFavorite } = useMaterialStore();

  const { mutate: finishMaterial } = useFinishMaterial();

  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);
  const [workbook, setWorkbook] = useState<IMaterial[]>([]);
  const [exercise, setExercise] = useState<IMaterial[]>([]);
  const [mindMap, setMindMap] = useState<IMaterial[]>([]);
  const [dossie, setDossie] = useState<IMaterial[]>([]);

  const { isContentLocked } = useAccess();

  useEffect(() => {
    if(materials?.length > 0) {
      filterMaterials()
    }
  }, [materials, selectedSubjects])

  useEffect(() => {
    if(module) {
      mutate(module)
    }
  }, [])

  const callbackFav = (data: IResponseFavorite) => {
    if(data.ok && module) {
      setFavorite(data.targetId as string);
      setStoredFavorite(data.targetId as string);
    }
  }

  const filterMaterials = () => {
    const aux = [...materials];
    let filteredData = aux;

    if(selectedSubjects?.length > 0) {
      filteredData = filteredData.filter((it) => selectedSubjects.includes(it.subjects[0].slug))
    }

    if(status?.length > 0) {
      if(status.includes('not_seen')) {
        filteredData = filteredData.filter((it) => !it.done)
      } else if(status.includes('seen')) {
        filteredData = filteredData.filter((it) => it.done)
      }
    }

    if(favorites) {
      filteredData = filteredData.filter((it) => it.favorite)
    }

    const workbookA:IMaterial[] = [], exerciseA:IMaterial[] = [], mindMapA:IMaterial[] = [], dossieA:IMaterial[] = [];

    filteredData.map((it) => {
      if (it.file_category === 'apostila') {
        workbookA.push(it);
      } else if (it.file_category === 'exercicios') {
        exerciseA.push(it);
      } else if (it.file_category === 'mapa_mental') {
        mindMapA.push(it);
      } else {
        dossieA.push(it);
      }
    })

    setAllMaterials(filteredData);
    setWorkbook(workbookA);
    setExercise(exerciseA);
    setMindMap(mindMapA);
    setDossie(dossieA);
  }

  function dispatchSetContentAsDoneEvent(id: string) {
    const setContentAsDone = new CustomEvent('setContentAsDone', {
      detail: {
        contentId: id,
      },
    });
    window.dispatchEvent(setContentAsDone);
  }

  const onFinish = (id: string, slug: string) => {
    if (!slug) return;

    const current = materials.find((it: IMaterial) => it.slug === slug);

    // Toggle: cada clique inverte o status. Usamos `viewStatus.viewed` da
    // resposta como fonte da verdade e atualizamos o store de forma imutável
    // (nova referência) para refletir na UI sem precisar recarregar a página.
    setLoadingSlug(slug);
    finishMaterial(
      {
        slug,
        type: materialViewTypeFromCategory(current?.file_category),
        origin: 'free_browsing',
      },
      {
        onSuccess: (res) => {
          const viewed = getViewedFromResponse(res);

          const updatedMaterials = materials.map((it: IMaterial) =>
            it.slug === slug ? { ...it, done: viewed } : it
          );
          setMaterials(updatedMaterials);

          if (viewed) dispatchSetContentAsDoneEvent(id);
        },
        onSettled: () => setLoadingSlug(null),
      }
    );
  };

  const materialSections = [
    {
      title: 'Apostilas',
      items: workbook,
      category: 'Apostila',
      categoryPlural: 'Apostilas'
    },
    {
      title: 'Lista de exercícios',
      items: exercise,
      category: 'Exercício',
      categoryPlural: 'Exercícios'
    },
    {
      title: 'Mapa mental',
      items: mindMap,
      category: 'Mapa mental',
      categoryPlural: 'Mapas mentais'
    },
    {
      title: 'Dossiê',
      items: dossie,
      category: 'Dossiê',
      categoryPlural: 'Dossiês'
    }
  ]

  if (isPending) {
    return (
      <Fragment>
        {Array.from({ length: 10 }, (_, index) => (
          <CardLoader key={index} />
        ))}
      </Fragment>
    );
  }

  return (
    <div className='flex flex-col gap-lg'>
      <FilterModal
        title="Filtrar materiais"
        onSubmit={() => filterMaterials()}
        reset={() => resetFilters()}
        modalName='normal-filter'
        type="materiais"
      />
      {materialSections.map((section) => (
        section.items.length > 0 && (
          <Fragment key={section.category}>
            <SectionHeader title={section.title}>
              <Typography size='small'>
                {section.items.length || 0}{' '}
                {section.items.length <= 1 ? section.category : section.categoryPlural}
              </Typography>
            </SectionHeader>
            {section.items.map((it, i) => {
              const locked = isContentLocked(it);
              return (
              <ContentCard
                color={discipline}
                key={i}
                title={it.title}
                done={it.done}
                firstViewedAt={it.firstViewedAt}
                favorite={it.favorite}
                icon='article'
                content='materiais'
                action={() =>
                  locked
                    ? openFreemiumUpsell()
                    : navigate(
                        url + `/assunto/${it.subjects[0].slug}/material/${it.slug}`
                      )
                }
                subject={getSubjectByFilter(it.subjects, selectedSubjects)}
                hasCallback
                callback={(data) => callbackFav(data)}
                slug={it.slug}
                onDone={(id: string, slug: string) => onFinish(id, slug)}
                doneLoading={loadingSlug === it.slug}
                id={it.id}
                locked={locked}
              >
                <Typography
                  size='small'
                  className=' transition delay-100 ease-in-out group-hover:text-card-color-default'
                >
                  {section.category}
                </Typography>
              </ContentCard>
              );
            })}
          </Fragment>
        )
      ))}
    </div>
  );
}